// src/Pages/Dashboard/Sales/ViewCreditNotes.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./ViewCreditNotes.css";
import { API_URL } from "../../../config";

const ViewCreditNotes = () => {
  const { creditNoteNumber } = useParams(); // Using creditNoteNumber in URL
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreditNote();
  }, [creditNoteNumber]);

  const fetchCreditNote = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/credit-notes/number/${creditNoteNumber}`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Not found");
      const data = await res.json();
      setCreditNote(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching credit note:", err);
      setLoading(false);
    }
  };
  
  if (loading) return <div className="loading">Loading Credit Note...</div>;
  if (!creditNote)
    return <div className="not-found">Credit Note not found</div>;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  return (
    <div className="view-credit-note-wrapper">
      <div className="view-header">
        <h1>Credit Note</h1>
        <div className="header-actions">
          <button onClick={() => window.print()} className="btn-print">
            Print
          </button>
          <Link to="/sales/credit-notes" className="btn-back">
            ← Back to List
          </Link>
        </div>
      </div>

      <div className="credit-note-document" id="credit-note-pdf">
        {/* Header */}
        <div className="doc-header">
          <div className="logo-placeholder">Your Company Logo</div>
          <div className="title-section">
            <h1>CREDIT NOTE</h1>
            <h2>{creditNote.creditNoteNumber}</h2>
          </div>
        </div>

        {/* Bill To & Details */}
        <div className="info-grid">
          <div className="bill-to">
            <h3>Bill To</h3>
            <p>
              <strong>{creditNote.customer?.name || "Walk-in Customer"}</strong>
            </p>
            <p>{creditNote.customer?.billingAddress || "—"}</p>
            <p>{creditNote.customer?.email || ""}</p>
            <p>GSTIN: {creditNote.customer?.gstin || "—"}</p>
          </div>

          <div className="credit-note-info">
            <div>
              <strong>Credit Note Date:</strong>{" "}
              {formatDate(creditNote.creditNoteDate)}
            </div>
            <div>
              <strong>Reference #:</strong> {creditNote.referenceNumber || "—"}
            </div>
            <div>
              <strong>Payment Terms:</strong> {creditNote.paymentTerms || "—"}
            </div>
            <div>
              <strong>Subject:</strong> {creditNote.subject || "—"}
            </div>
          </div>
        </div>

        {/* Items Table */}
        <table className="items-table-view">
          <thead>
            <tr>
              <th>#</th>
              <th>Item & Description</th>
              <th>Account</th>
              <th className="text-right">Quantity</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {creditNote.items && creditNote.items.length > 0 ? (
              creditNote.items.map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <div>
                      <strong>{item.itemDetails || "—"}</strong>
                    </div>
                  </td>
                  <td>{item.account}</td>
                  <td className="text-right">{item.quantity}</td>
                  <td className="text-right">
                    ₹{Number(item.rate).toFixed(2)}
                  </td>
                  <td className="text-right">
                    ₹{Number(item.amount).toFixed(2)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No items added
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div className="totals-section">
          <div className="notes-section">
            <div>
              <strong>Customer Notes:</strong>
              <br />
              {creditNote.customerNotes || "No notes added."}
            </div>
            <div style={{ marginTop: "20px" }}>
              <strong>Terms & Conditions:</strong>
              <br />
              {creditNote.termsConditions || "Standard terms apply."}
            </div>
          </div>

          <div className="amount-summary">
            <div>
              <span>Sub Total</span>{" "}
              <strong>₹{Number(creditNote.subTotal || 0).toFixed(2)}</strong>
            </div>
            <div>
              <span>Discount</span>{" "}
              <strong>- ₹{Number(creditNote.discount || 0).toFixed(2)}</strong>
            </div>
            <div>
              <span>Tax (TDS/TCS)</span>{" "}
              <strong>₹{Number(creditNote.tax || 0).toFixed(2)}</strong>
            </div>
            <div>
              <span>Adjustment</span>{" "}
              <strong>₹{Number(creditNote.adjustment || 0).toFixed(2)}</strong>
            </div>
            <div className="total-row">
              <span>
                <strong>Total Amount</strong>
              </span>
              <strong className="grand-total">
                ₹{Number(creditNote.total || 0).toFixed(2)}
              </strong>
            </div>
          </div>
        </div>

        <div className="status-footer">
          <span className={`status-badge ${creditNote.status?.toLowerCase()}`}>
            {creditNote.status || "Draft"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ViewCreditNotes;
